import {
  SearchInsuranceProvider,
  CreateInsuranceProvider,
  DeleteInsuranceProvider,
  UpdateInsuranceProviderName,
  InsuranceProvider,
  CreateInsuranceProviderInput,
  UpdateInsuranceProviderNameInput,
  GetInsuranceProvider,
  UpdateInsuranceProviderState
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/InsuranceProviders/PathItems";
import { SearchInput } from "../../PetKey/Main/Model/1_0_Initial/Core/PathItems";
import { Published } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import { applyStateTransition, transitionState } from "../../../utils/GeneralHelpers";

export function mockInsuranceProviderInterceptors(builder: TransportAbstractionBuilder) {
  const dbProviders: InsuranceProvider[] = [];
  const dbProvidersPublished: InsuranceProvider[] = [];

  const getProvider = (ctx: string | TransportContext): Result<InsuranceProvider> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('insuranceProviderId');
    const provider = dbProviders.find(p => p.id === id);
    if (!provider) {
      return Result.notFound('Backoffice.InsuranceProviders.NotFound', `InsuranceProvider with id ${id} not found`);
    }
    return Result.ok(provider);
  };

  const saveProvider = (result: Result<InsuranceProvider>, ctx: TransportContext): Result<InsuranceProvider> => {
    if (!result.success) {
      return result;
    }
    const provider = result.value;
    const existingIndex = dbProviders.findIndex(p => p.id === provider.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbProvidersPublished.findIndex(p => p.id === provider.id);
      dbProviders[existingIndex] = applyStateTransition(dbProvidersPublished, existingPublishedIndex, result.meta, provider, provider.state, (itm, newState) => {
        itm.state = newState;
        return itm;
      });
    } else {
      dbProviders.push(provider);
    }
    return Result.ok(provider);
  };

  return builder
    .intercept(new GetInsuranceProvider().handle(async (input, ctx) => {
      return getProvider(ctx);
    }))
    .intercept(new SearchInsuranceProvider().handle(async (input: SearchInput, ctx) => {
      const filtered = dbProviders
        .slice(input.offset, input.offset + input.limit)
        .filter(p =>
          p.name.toLowerCase().includes(input.query.toLowerCase())
        );
      return Result.ok(filtered);
    }))
    .intercept(new CreateInsuranceProvider().handle(async (input: CreateInsuranceProviderInput, ctx) => {
      const newProvider: InsuranceProvider = {
        id: input.insuranceProviderId ?? generateUUID(),
        name: input.name,
        state: { state: 'draft' } as Published
      };
      return saveProvider(Result.ok(newProvider), ctx);
    }))
    .intercept(new DeleteInsuranceProvider().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('insuranceProviderId');
      const idx = dbProviders.findIndex(p => p.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.InsuranceProviders.NotFound', `InsuranceProvider with id ${id} not found`);
      }
      dbProviders.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateInsuranceProviderName().handle(async (input: UpdateInsuranceProviderNameInput, ctx) => {
      return saveProvider(getProvider(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateInsuranceProviderState().handle(async (input, ctx) => {
      return saveProvider(getProvider(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }));
}
